import { api } from './api';

// houseFill — "inventory never renders blank" (promotions spec §6). A placement
// with no booking and no default_content_ref pulls LIVE HypeTunes content that
// matches its content type: featured track art, an artist portrait, a music-video
// poster. So the city always looks excited about something, and an advertiser
// evaluating placements never sees a ghost town.
//
// HOUSE FILL IS THE UNSOLD/UNBOOKED CASE ONLY, and it is deliberately RANDOM —
// it exists so the city never looks dead. PAID + PLANNED placements are never
// chosen here: they are set explicitly PER SURFACE and stored in the database
// (`default_content_ref` today, a scheduled `promotions` booking next), and they
// always win over house fill in the renderer's resolution order.
//
// STILLS ONLY for now — animated fills wait for the video decode pool (§7), so
// house fill can never blow the concurrent-decode budget.
//
// Pools are fetched ONCE per session, SHUFFLED, and shared by every surface; each
// placement then picks by id, so boards differ from each other and from the
// billboards/record store, without flickering within a session.

let pools = null;      // { tracks, artists, videos, interviews, broadcasts }
let loading = null;
const subs = new Set();

// Shuffle each pool ONCE per session (Fisher-Yates). Without this, picking by
// `placement_id % length` always lands on the FIRST few items — the same ones the
// city billboards and the record store already show, so the whole town repeated a
// handful of covers. Shuffled: house surfaces spread across the entire catalogue
// and vary between visits, while staying stable within a session (no flicker).
function shuffle(a) {
  const out = [...a];
  for (let i = out.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

async function load() {
  const out = { tracks: [], artists: [], videos: [], interviews: [], broadcasts: [], clips: [] };
  // Artists come from the FEATURED-ARTIST showcase (is_featured_artist = 1), not
  // "any active avatar" — house fill should promote the artists we actually
  // feature. That endpoint also hands back signed image URLs.
  const [tracks, artists, videos, episodes, clips] = await Promise.allSettled([
    api.featuredTracks(), api.featuredArtists(), api.musicVideos(24), api.podcastEpisodes(24),
    api.hypeClips(24),
  ]);
  // Each item carries BOTH a still poster and (when it exists) an animated
  // variant — "prefer animation when available", but the renderer only plays it
  // when the video pool grants a slot, so the decode budget is never blown.
  // `name`/`sub` drive the RENDERED name plate on the surface (never baked into
  // the art) — so one creative reads correctly on every board and the copy can
  // change without regenerating the image.
  if (tracks.status === 'fulfilled') {
    out.tracks = (tracks.value?.tracks || [])
      .filter((t) => t.artwork_url)
      .map((t) => ({
        still: t.artwork_url,
        animated: t.artwork_animation_url || t.artwork_animated_url || null,
        name: t.avatar_name || t.title || '', sub: t.avatar_name ? t.title : '',
        avatarId: t.avatar_id || null,
      }));
  }
  if (artists.status === 'fulfilled') {
    // BREATHING IMAGES. The showcase hands back `profile_animation_url` — the
    // avatar's profile animation, the same thing the site calls "breathing" —
    // plus the flags saying whether it is finished and switched on.
    //
    // This used to read `profile_animation_gif_url`/`animation_gif_url`, neither
    // of which that endpoint returns, so `animated` was ALWAYS null and no
    // featured-artist placement could ever breathe. It failed silently because a
    // missing animation is a legal state — the still just shows.
    //
    // A pending animation is a URL that isn't finished rendering yet, so it is
    // treated as absent rather than played half-made.
    const usable = (a) => a.profile_animation_url
      && a.profile_animation_enabled !== 0 && a.profile_animation_enabled !== false
      && a.profile_animation_status !== 'pending' && a.profile_animation_status !== 'failed';
    out.artists = (artists.value?.artists || [])
      .filter((a) => a.profile_image_url)
      .map((a) => ({
        still: a.profile_image_url,
        animated: usable(a) ? a.profile_animation_url : null,
        name: a.name || '', sub: a.main_genre || '',
        avatarId: a.avatar_id || null,
      }));
    const breathing = out.artists.filter((a) => a.animated).length;
    console.log(`[houseFill] ${breathing}/${out.artists.length} featured artists have a breathing image`);
  }
  if (videos.status === 'fulfilled') {
    out.videos = (videos.value?.videos || [])
      .filter((v) => v.gcs_url || v.thumbnail_url)
      .map((v) => ({
        still: v.thumbnail_url || v.poster_url || null,
        animated: v.gcs_url || null,
        name: v.avatar_name || v.track_title || v.title || '', sub: v.track_title || '',
        avatarId: v.avatar_id || null,
      }));
  }
  // ── PODCASTS, SPLIT BY WHETHER THERE IS A GUEST ───────────────────────────
  //
  // A screen set to "Interview podcast" was showing MUSIC VIDEOS (Khari,
  // 2026-08-04, placing one in the salon). It had no pool of its own — poolFor()
  // sent both podcast types straight to `pools.videos`, so the surface was doing
  // exactly what it was told and the label was a lie.
  //
  // `guest_avatar_id` is the discriminator, and it is the only per-episode one
  // there is: `show_format` sits on the host SET, so a set that produces both
  // formats reports the same value for either (PodcastService.getEpisodes says
  // so at the query). NULL = banter, i.e. the news show.
  //
  // Art: an interview has a host+guest composite in `cover_image_url`; a banter
  // episode is documented as NULL there and falls back to a host portrait, which
  // is the station's face and the right thing on a wall. Anything with no image
  // at all is dropped rather than placed as a blank rectangle.
  if (episodes.status === 'fulfilled') {
    const items = (episodes.value?.episodes || [])
      .map((e) => ({
        still: e.cover_image_url || e.host_a_image || e.host_b_image || null,
        // Covers are stills. The visual_timeline slideshow (services/newsPodcast)
        // is a different, heavier surface — NewsTv plays that; a placement quad
        // shows the episode's face.
        animated: null,
        name: e.show_name || e.title || '',
        sub: e.title || e.topic || '',
        avatarId: e.guest_avatar_id || e.host_a_avatar_id || null,
        interview: !!e.guest_avatar_id,
      }))
      .filter((e) => e.still);
    out.interviews = items.filter((e) => e.interview);
    out.broadcasts = items.filter((e) => !e.interview);
  }

  // ── HYPECLIPS, same bug one line over ─────────────────────────────────────
  //
  // A 'hypeclip' placement also fell through to `pools.videos` — music videos —
  // even though `api.hypeClips` already existed and CityModel was using it for
  // the tall city signs. So the vertical surfaces were showing 16:9 music videos
  // letterboxed into a 9:16 frame. Found while fixing the podcast case; it is
  // the same root cause (a content type with no pool of its own) and the same
  // fix, so it is corrected here rather than left as a matching bug.
  if (clips.status === 'fulfilled') {
    out.clips = (clips.value?.clips || [])
      .filter((c) => c.gcs_url || c.thumbnail_url)
      .map((c) => ({
        still: c.thumbnail_url || c.poster_url || null,
        animated: c.gcs_url || null,
        name: c.avatar_name || c.title || '', sub: c.track_title || c.title || '',
        avatarId: c.avatar_id || null,
      }));
  }

  pools = {
    tracks: shuffle(out.tracks), artists: shuffle(out.artists), videos: shuffle(out.videos),
    interviews: shuffle(out.interviews), broadcasts: shuffle(out.broadcasts), clips: shuffle(out.clips),
  };
  console.log(`[houseFill] pools ready — ${pools.tracks.length} tracks · ${pools.artists.length} artists`
    + ` · ${pools.videos.length} videos · ${pools.interviews.length} interviews · ${pools.broadcasts.length} broadcasts`,
    { tracksFailed: tracks.status !== 'fulfilled', artistsFailed: artists.status !== 'fulfilled',
      videosFailed: videos.status !== 'fulfilled', episodesFailed: episodes.status !== 'fulfilled' });
  // An empty podcast pool is worth saying out loud: the surface will fall back to
  // something else entirely, which is exactly the bug this split fixes and would
  // otherwise look identical to it.
  if (!pools.interviews.length) console.warn('[houseFill] no INTERVIEW episodes (published, with a guest and an image) — interview-podcast surfaces will fall back');
  if (!pools.broadcasts.length) console.warn('[houseFill] no BANTER/news episodes with an image — news-broadcast surfaces will fall back');
  subs.forEach((fn) => { try { fn(); } catch { /* noop */ } });
  return pools;
}

export function ensureHouseFill() {
  if (pools) return Promise.resolve(pools);
  if (!loading) loading = load().catch(() => { pools = { tracks: [], artists: [], videos: [] }; return pools; });
  return loading;
}

// Re-render hook: components subscribe so they paint once the pools land.
export function onHouseFill(fn) { subs.add(fn); return () => subs.delete(fn); }

// ── Rotation ──────────────────────────────────────────────────────────────
// House fill used to be `placement_id % pool.length`: stable forever, so every
// surface showed the same picture for the life of the world and the city read
// as a photograph. It is standing in for advertising — the one thing about real
// advertising you can rely on is that it changes.
//
// An EPOCH does that without any state: every surface derives its own item from
// the current time bucket, so they all turn over together, nothing has to be
// stored, and a visitor arriving later sees what everyone else is seeing.
const ROTATE_MS = 45_000;
const epochNow = () => Math.floor(Date.now() / ROTATE_MS);
let epoch = epochNow();
if (typeof window !== 'undefined') {
  // One timer for the whole world, not one per surface. Ticks a little faster
  // than the epoch so a bucket boundary is never missed by a whole cycle.
  setInterval(() => {
    const e = epochNow();
    if (e === epoch) return;
    epoch = e;
    // Say so. A rotation that silently does nothing (because a pool holds a
    // single item) is indistinguishable from one that never fires.
    const c = pools ? `${pools.tracks.length}t/${pools.artists.length}a/${pools.videos.length}v` : 'no pools';
    console.log(`[houseFill] epoch ${e} — rotating (${c})`);
    subs.forEach((fn) => { try { fn(); } catch { /* noop */ } });
  }, ROTATE_MS / 4);
}

// Spread neighbours apart. Adding the id would march consecutive placements
// through the pool in lockstep — a row of billboards showing consecutive items,
// then all shifting by one together. Hashing scatters them instead.
const warnedSingle = new WeakSet();
const spread = (n) => {
  const x = Math.sin(n * 12.9898) * 43758.5453;
  return Math.floor((x - Math.floor(x)) * 9973);
};

// Which pool a placement draws from, by its declared content type (falling back
// to aspect: square → track art, portrait → artist, wide → video poster).
// ── BOOKED CAMPAIGNS: `quickshout:<advertiserRef>` ──────────────────────────
//
// Khari, 2026-08-06: "In the Ad placement, we have a quickshout type, and an ID
// to enter. the ad placed will show and rotate the ads published by that ID."
//
// It rides house fill rather than `default_content_ref` for one reason: that
// column PINS a surface to a single image and explicitly stops rotating (see
// PlacementLayer's "is PINNED — not rotating"). A campaign is a SET, and
// rotation is the whole ask — which is exactly what this file already is.
//
// Fetched lazily, per advertiser, the first time a surface asks. Until it
// lands the placement house-fills as normal, so a slow network is a wall
// showing HypeTunes content for a second, never a dark rectangle.
// ── AND IT REFRESHES, BECAUSE PUBLISHING IS A LIVE ACT ──────────────────────
// The house pools are fetched once per session, which is right for a catalogue
// that changes daily. A campaign is different: somebody presses Publish in
// QuickShout and then walks over to look at the wall. Cached for the session,
// that new creative would not appear until a browser reload — which turns the
// best thing about this feature into a refresh instruction.
//
// So a pool goes stale after one rotation epoch (45s) and refetches — but the
// CACHED LIST KEEPS SERVING while it does. Clearing first would drop the
// surface back to house content for the length of a round trip, so every wall
// in town would blink to an album cover every 45 seconds. Swap on arrival, or
// do not swap.
const campaigns = new Map();   // "source:ref" → { list, epoch } (empty list = fetched, none)
const campaignsLoading = new Set();

function campaignPool(key) {
  const held = campaigns.get(key);
  const stale = held && held.epoch !== epoch;
  // A STALE POOL STILL SERVES. Khari, 2026-08-06: "my placed ad started as a
  // quickshout and then started rotating the house fill."
  //
  // This function used to fall through to `return null` on a stale pool — the
  // same line that correctly means "nothing fetched yet, house-fill for now" —
  // so at the first 45s epoch the wall dropped to album covers while the
  // refetch ran, and every epoch after that did it again. The refresh has to
  // kick WITHOUT taking the current answer away; the swap belongs in the
  // `.then`, and nowhere else.
  //
  // Only a genuinely unknown campaign returns null, and only until it answers.
  if (held && !stale) return held.list;
  if (!campaignsLoading.has(key)) {
    campaignsLoading.add(key);
    const [source, ref] = [key.slice(0, key.indexOf(':')), key.slice(key.indexOf(':') + 1)];
    api.adCreatives(ref, source)
      .then((d) => {
        // `animated` is the moving version, and it costs nothing extra here: the
        // renderer already treats `{ still, animated }` as one item, proxies the
        // video, pools it against MAX_VIDEOS and drops to the still on mobile.
        // Filtering on `still` keeps that guarantee — a creative with no poster
        // would be a black rectangle on every phone.
        const list = (d?.creatives || []).filter((c) => c.still).map((c) => ({
          still: c.still,
          animated: c.animated || null,
          name: c.title || c.companyName || null,
          sub: c.title && c.companyName ? c.companyName : null,
        }));
        const before = campaigns.get(key)?.list?.length ?? -1;
        campaigns.set(key, { list, epoch });
        if (!list.length) console.warn(`[houseFill] campaign "${key}" has NO creatives — surfaces asking for it fall back to house content`);
        else if (before !== list.length) console.log(`[houseFill] campaign "${key}" — ${list.length} creative(s) in rotation`);
        // Paint now rather than at the next epoch: a surface that resolved
        // before the fetch landed is currently showing house fill. Only worth a
        // repaint when the set actually changed — a refresh that found the same
        // creatives should cost nothing.
        if (before !== list.length) subs.forEach((fn) => { try { fn(); } catch { /* noop */ } });
      })
      .catch((e) => {
        // Hold what we had, and STAMP IT TO THIS EPOCH so a failing endpoint
        // does not re-request four times a second for the rest of the session.
        // A failed refresh must never blank a wall that is already right; only
        // a first fetch that fails caches empty.
        const prev = campaigns.get(key);
        campaigns.set(key, { list: prev?.list || [], epoch });
        console.warn(`[houseFill] campaign "${key}" failed to load:`, e.message);
      })
      .finally(() => campaignsLoading.delete(key));
  }
  // Whatever we hold, even if it is being refreshed right now. Null ONLY when
  // this campaign has never answered — that is the case house fill is for.
  return held ? held.list : null;
}

// ── THE LATEST POSTS: a board that shuffles through what artists just posted ──
//
// 2026-11-01, an artist's idea for the HypeFlyer print shop, via Khari: "a
// billboard/ad placement board that shuffles through the latest posts. We
// should use the 1:1 size, since most of them use that size. We would want a
// way to read the caption as well. perhaps clicking on it would toggle the
// caption." The content type is `latest_posts` (PlacementPanel). Its pool is the
// public feed (api.latestPosts): posts with a picture (a carousel's first
// slide), newest first, each carrying its caption for the click (PlacementLayer
// PostCaption). Like a campaign it goes stale after a rotation epoch and
// refetches WHILE STILL SERVING, so a new post appears within a minute without
// the board ever blinking to something else.
//
// It turns over faster than house fill (POSTS_MS, not 45 s) and in order — newest
// first, round again — because it is a feed, not a poster.
const POSTS_MS = 12_000;
const postsEpochNow = () => Math.floor(Date.now() / POSTS_MS);
let postsEpoch = postsEpochNow();
let posts = null;              // { list, epoch } once fetched
let postsLoading = false;
let postsAsked = false;
if (typeof window !== 'undefined') {
  setInterval(() => {
    if (!postsAsked) return;   // nobody shows posts: nothing to turn over
    const e = postsEpochNow();
    if (e === postsEpoch) return;
    postsEpoch = e;
    subs.forEach((fn) => { try { fn(); } catch { /* noop */ } });
  }, POSTS_MS / 4);
}

const firstImage = (p) => {
  let slides = p.carousel_images;
  if (typeof slides === 'string') { try { slides = JSON.parse(slides); } catch { slides = null; } }
  const slide = Array.isArray(slides) ? slides.find((s) => s?.image_url || s?.url) : null;
  return p.image_url || slide?.image_url || slide?.url || null;
};

function postsPool() {
  postsAsked = true;
  const stale = posts && posts.epoch !== epoch;
  if ((!posts || stale) && !postsLoading) {
    postsLoading = true;
    api.latestPosts(24)
      .then((d) => {
        const list = (d?.posts || [])
          .map((p) => ({
            still: firstImage(p),
            animated: null,
            name: p.avatar_name || '',
            sub: p.avatar_handle ? `@${p.avatar_handle}` : '',
            caption: String(p.content || '').trim(),
            avatarId: p.avatar_id || null,
            postId: p.post_id || null,
          }))
          .filter((p) => p.still);
        const changed = (posts?.list || []).map((x) => x.postId).join() !== list.map((x) => x.postId).join();
        posts = { list, epoch };
        if (!list.length) console.warn('[houseFill] no recent posts with a picture — "Latest posts" boards fall back to track art');
        if (changed) subs.forEach((fn) => { try { fn(); } catch { /* noop */ } });
      })
      .catch((e) => {
        posts = { list: posts?.list || [], epoch };   // hold what we had; no retry storm
        console.warn('[houseFill] latest posts failed to load:', e.message);
      })
      .finally(() => { postsLoading = false; });
  }
  return posts ? posts.list : null;
}

function poolFor(placement) {
  // Checked BEFORE the `pools` guard: a booked campaign does not depend on the
  // house pools having loaded, and making it wait would leave paid inventory
  // blank while free inventory decides what it has.
  const policy = placement.content_policy || [];
  const booked = policy.find((p) => typeof p === 'string' && p.includes(':'));
  if (booked) {
    const list = campaignPool(booked);
    if (list?.length) return list;
    // Still loading, or an empty/typo'd campaign — fall through to house fill.
  }
  // The feed, likewise, never waits on the house pools.
  if (policy[0] === 'latest_posts') { const l = postsPool(); if (l?.length) return l; }
  if (!pools) return [];
  // Every branch falls back to another pool rather than rendering nothing.
  const type = policy[0];
  const first = (...lists) => lists.find((l) => l?.length) || [];
  if (type === 'featured_track') return first(pools.tracks, pools.artists);
  if (type === 'featured_artist') return first(pools.artists, pools.tracks);
  // PODCASTS SHOW PODCASTS. Both of these used to return `pools.videos`, so an
  // "Interview podcast" screen played music videos. Each now draws its own kind
  // first and only then falls back — and the fallback prefers the OTHER podcast
  // pool, because a news show on an interview screen is still the right medium.
  if (type === 'interview_podcast') return first(pools.interviews, pools.broadcasts, pools.artists, pools.tracks);
  if (type === 'news_broadcast') return first(pools.broadcasts, pools.interviews, pools.videos, pools.tracks);
  if (type === 'hypeclip') return first(pools.clips, pools.videos, pools.artists, pools.tracks);
  if (type === 'latest_posts') return pools.tracks;   // no posts yet: track art, never a blank board
  const a = placement.aspect;
  if (a === '1:1') return pools.tracks;
  if (a === '3:4' || a === '9:16') return pools.artists.length ? pools.artists : pools.tracks;
  return pools.videos.length ? pools.videos : pools.tracks;
}

/** Pool sizes, for the admin panel's content readout. */
export function poolCounts() {
  return pools
    ? {
      tracks: pools.tracks.length, artists: pools.artists.length, videos: pools.videos.length,
      interviews: pools.interviews.length, broadcasts: pools.broadcasts.length,
      clips: pools.clips.length, ready: true,
    }
    : { tracks: 0, artists: 0, videos: 0, interviews: 0, broadcasts: 0, clips: 0, ready: false };
}

/** House-fill media for this placement: { still, animated } — or null if no pool. */
export function houseFillItem(placement) {
  const list = poolFor(placement);
  if (!list?.length) return null;
  // One item in the pool means every surface drawing from it shows that item,
  // forever, however the index is computed. Worth saying out loud — it reads as
  // "the rotation is broken" when it is really "there is nothing to rotate".
  if (list.length === 1 && !warnedSingle.has(list)) {
    warnedSingle.add(list);
    console.warn('[houseFill] a pool holds a single item — nothing to rotate between');
  }
  // A posts board walks the feed in order, newest first, on its own faster clock.
  if ((placement.content_policy || [])[0] === 'latest_posts' && list === posts?.list) return list[postsEpoch % list.length];
  const id = Math.abs(Number(placement.placement_id) || 0);
  const i = (epoch + spread(id)) % list.length;
  return list[i];
}
